<template>
  <div class="find-supplier-page">
    <section class="brand-filter">
      <div class="brand-filter__fields">
        <div class="filter-field filter-field--brand">
          <span class="field-label">品牌</span>
          <el-tooltip
            v-model:visible="brandIntroVisible"
            :disabled="brandDropdownVisible || !filters.brandId"
            trigger="hover"
            effect="dark"
            placement="right-start"
            :fallback-placements="['left-start', 'bottom-start']"
            popper-class="brand-intro-tooltip"
          >
            <div
              class="brand-select-tooltip-trigger"
              @mouseenter="brandIntroVisible = true"
              @mouseleave="brandIntroVisible = false"
              @mousedown="brandIntroVisible = false"
            >
              <el-popover
                v-model:visible="brandDropdownVisible"
                trigger="click"
                placement="bottom-start"
                :width="700"
                :show-arrow="false"
                popper-class="brand-picker-popper"
                @hide="handleBrandDropdownHide"
              >
                <div class="brand-picker">
                  <div class="brand-picker__header">
                    <button
                      type="button"
                      :class="{ active: brandSortMode === 'simple' }"
                      @click.stop="changeBrandSortMode('simple')"
                    >
                      品牌简称<span class="sort-arrow">↓</span>
                    </button>
                    <button
                      type="button"
                      :class="{ active: brandSortMode === 'full' }"
                      @click.stop="changeBrandSortMode('full')"
                    >
                      品牌全称<span class="sort-arrow">↓</span>
                    </button>
                    <span>产品数量</span>
                  </div>
                  <div v-if="brandDisplayGroups.length" class="brand-picker__body">
                    <div
                      v-for="group in brandDisplayGroups"
                      :key="group.key"
                      class="brand-picker__group"
                    >
                      <button
                        type="button"
                        class="brand-picker__group-title"
                        @click.stop="toggleBrandGroup(group.key)"
                      >
                        <span class="group-arrow">{{ isBrandGroupExpanded(group.key) ? '▼' : '▶' }}</span>
                        <span>{{ group.label }}</span>
                        <b>{{ group.children.length }}</b>家
                      </button>
                      <button
                        v-for="brand in group.children"
                        v-show="isBrandGroupExpanded(group.key)"
                        :key="brand.id"
                        type="button"
                        class="brand-picker__row"
                        :class="{ selected: String(filters.brandId) === String(brand.id) }"
                        @click.stop="selectBrand(brand)"
                      >
                        <strong>{{ brand.simpleName }}</strong>
                        <span>{{ brand.categoryName }}</span>
                        <span class="brand-picker__sku-num">共计{{ brand.skuNums || 0 }}个</span>
                      </button>
                    </div>
                  </div>
                  <div v-else class="brand-picker__empty">暂无匹配品牌</div>
                </div>
                <template #reference>
                  <div class="brand-input-shell">
                    <el-input
                      v-model="brandSearchKeyword"
                      class="brand-picker-input"
                      size="small"
                      clearable
                      placeholder="请输入品牌简称或品牌全称"
                      @focus="handleBrandInputFocus"
                      @input="handleBrandSearchInput"
                      @clear="clearBrandSelection"
                    />
                  </div>
                </template>
              </el-popover>
            </div>
            <template #content>
              <div class="brand-intro-tooltip__content">
                <p><span>品牌简称：</span>{{ selectedBrandInfo.simpleName }}</p>
                <p><span>品牌全称：</span>{{ selectedBrandInfo.categoryName }}</p>
                <p><span>品牌介绍：</span>{{ selectedBrandInfo.brandIntro }}</p>
              </div>
            </template>
          </el-tooltip>
        </div>
        <div class="filter-field filter-field--series">
          <span class="field-label">系列</span>
          <tree-select
            v-model="filters.series"
            :key="filters.brandId"
            :options="seriesOptions"
            :clearable="true"
            :disable-branch-nodes="true"
            :searchable="true"
            :defaultExpandLevel="0"
            :flatten-search-results="true"
            :load-options="loadSeriesOptions"
            placeholder="请选择系列"
            @select="handleSeriesChange"
          />
        </div>
        <div class="filter-field filter-field--area">
          <span class="field-label">区域</span>
          <div class="area-input-shell">
            <el-input
              v-if="isRegionalSupplierType"
              class="local-area-input"
              :model-value="currentUserProvince || '未设置本地区域'"
              readonly
              size="small"
              aria-label="个人本地区域"
            />
            <el-cascader
              v-else
              v-model="filters.area"
              class="area-selector"
              :options="areaOptions"
              :props="areaCascaderProps"
              :show-all-levels="false"
              filterable
              clearable
              size="small"
              placeholder="请选择区域"
              @change="handleTopAreaChange"
            />
          </div>
          <span class="vip-tip">{{ isRegionalSupplierType ? '（个人本地区域）' : '（升级为VIP可选择区域）' }}</span>
        </div>
      </div>
      <div class="brand-filter__links">
        <span @click="openBrandLink('companyIntroductionUrl')">公司介绍</span>
        <span @click="openBrandLink('documentDownloadUrl')">资料下载</span>
        <span class="notice" @click="openBrandLink('priceIncreaseNoticeUrl')"><i>NEW</i><b>⚡</b>涨价通知</span>
      </div>
    </section>

    <section v-if="supplierTabs.length" class="supplier-panel">
      <div class="supplier-tabs">
        <el-tooltip
          v-for="item in supplierTabs"
          :key="item.value"
          :content="item.brief"
          placement="top"
          effect="light"
        >
          <button
            type="button"
            :class="{
              active: filters.supplierType === item.value,
              'factory-entry': item.value === 'factory'
            }"
            @click="changeSupplierType(item.value)"
          >
            {{ item.label }}
          </button>
        </el-tooltip>
      </div>
      <div class="benefit-banner">
        <div>
          <h3>{{ currentCategory.title }}</h3>
          <p>{{ currentCategory.description }}</p>
        </div>
        <div class="banner-pattern" aria-hidden="true"></div>
      </div>
    </section>

    <section class="search-toolbar">
      <el-input
        v-model="filters.keyword"
        class="keyword-input"
        size="small"
        clearable
        placeholder="查找公司名称、地址、姓名、手机号..."
        @keyup.enter="applyFilters"
      />
      <div class="month-range">
        <el-date-picker
          v-model="filters.startMonth"
          type="month"
          size="small"
          format="YYYY-MM"
          value-format="YYYY-MM"
          placeholder="开始月份"
          @change="applyFilters"
        />
        <span class="date-split">至</span>
        <el-date-picker
          v-model="filters.endMonth"
          type="month"
          size="small"
          format="YYYY-MM"
          value-format="YYYY-MM"
          placeholder="结束月份"
          @change="applyFilters"
        />
      </div>
      <el-select v-model="filters.companyType" size="small" placeholder="全部类型" @change="applyFilters">
        <el-option v-for="item in activeCompanyTypeOptions" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select
        v-if="showSecondaryBrandAreaFilter"
        v-model="filters.filterBrand"
        size="small"
        placeholder="全部品牌"
        @change="handleFilterBrandChange"
      >
        <el-option v-for="item in activeFilterBrandOptions" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select
        v-if="showSecondaryBrandAreaFilter || isRegionalSupplierType"
        v-model="filters.filterArea"
        size="small"
        placeholder="全部区域"
        @change="applyFilters"
      >
        <el-option v-for="item in activeFilterAreaOptions" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select v-model="filters.relationship" size="small" placeholder="全部关系" @change="applyFilters">
        <el-option v-for="item in activeRelationshipOptions" :key="item" :label="item" :value="item" />
      </el-select>
      <div class="toolbar-actions">
        <button type="button" class="query-btn" @click="applyFilters">查询</button>
        <button type="button" class="reset-btn" @click="resetFilters">重置</button>
        <button type="button" class="collapse-btn" @click="toggleAllSuppliers">
          全部{{ filtersCollapsed ? '展开' : '收起' }}
          <span :class="{ rotated: filtersCollapsed }">⌃</span>
        </button>
      </div>
    </section>

    <div v-loading="supplierLoading" class="supplier-content">
      <h2 v-if="isRegionGroupedType && filteredFeaturedSuppliers.length" class="list-title">
        推荐{{ filters.supplierType === 'factory' ? '厂家' : (filters.supplierType === 'localAgent' ? '代理商' : '供应商') }}
        <span v-if="recommendedSupplierCount !== null" class="region-supplier-count">（共 {{ recommendedSupplierCount }} 家）</span>
      </h2>
      <article
        v-for="supplier in filteredFeaturedSuppliers"
        :key="supplier.id"
        class="supplier-card supplier-card--recommended"
        :class="{ 'is-collapsed': !supplier.expanded }"
      >
        <span class="recommend-ribbon">{{ supplier.ribbon || '推荐供应商' }}</span>
        <supplier-card-content
          :supplier="supplier"
          @toggle="toggleSupplier(supplier)"
          @select-tab="selectSupplierTab(supplier, $event)"
        />
      </article>

      <section v-for="group in standardRegionGroups" :key="group.key" class="supplier-region-group">
        <h2 class="list-title">
          {{ group.title }}
          <span v-if="isRegionGroupedType && group.totalCount !== null" class="region-supplier-count">（共 {{ group.totalCount }} 家）</span>
        </h2>
        <article
          v-for="supplier in group.suppliers"
          :key="supplier.id"
          class="supplier-card"
          :class="{ 'is-collapsed': !supplier.expanded }"
        >
          <supplier-card-content
            :supplier="supplier"
            @toggle="toggleSupplier(supplier)"
            @select-tab="selectSupplierTab(supplier, $event)"
          />
        </article>
      </section>

      <div
        v-if="!supplierLoading && !filteredFeaturedSuppliers.length && !filteredStandardSuppliers.length"
        class="empty-result"
      >
        暂未找到符合条件的{{ filters.supplierType === 'customer' ? '客户' : '供应商' }}
      </div>

      <div
        v-show="['all', 'factory', 'recommended', 'localAgent', 'mine', 'customer'].includes(filters.supplierType)"
        ref="supplierLoadTrigger"
        class="supplier-load-trigger"
      >
        <span v-if="supplierLoading">正在加载{{ filters.supplierType === 'customer' ? '客户' : '供应商' }}...</span>
        <span v-else-if="hasMoreSupplierPages">继续下滑加载更多</span>
        <span v-else-if="supplierPage.totalCount > 0">已加载全部 {{ supplierPage.totalCount }} {{ filters.supplierType === 'customer' ? '家客户' : '家供应商' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import SupplierCardContent from '../components/SupplierCardContent.vue'
import { mapActions } from 'vuex'
import { getLetterSortData, handleTreeData } from '@/common/js/common.js'
import area from '@/common/json/area.json'

const createStats = () => [
  { label: '供应商', value: '112 家' },
  { label: '客户', value: '56' },
  { label: '代理品牌', value: '2' },
  { label: '自动接单', value: '345' },
  { label: '报价单', value: '1211' },
  { label: '销售订单', value: '3' },
  { label: '询价单', value: '1211' },
  { label: '采购订单', value: '444' },
  { label: '经营品牌', value: '3' }
]

const detailTabs = ['基础信息', '企业信息', '公司资质', '公司照片', '联系人', '代理品牌', '经营品牌', '盘雷数据', '我的库存']
const customerDetailTabs = ['基础信息', '企业信息', '公司资质', '公司照片', '联系人', '采购偏好']

const createSupplier = (id, options = {}) => ({
  id,
  name: options.name || '成都兴汇川自动化工程有限公司',
  fullName: options.fullName || '武汉海得电气科技有限公司',
  code: options.code || 'PLB0000002553',
  address: options.address || '湖北省武汉市洪山区珞狮路112号匠心城中航广场A座4楼403室',
  contact: options.contact || '谭XX',
  phone: options.phone || '187****4756',
  types: options.types || '盘柜厂（PB）/元器件/低压柜/配电箱/成套',
  agentBrandLabel: options.agentBrandLabel || '代理品牌',
  businessBrandLabel: options.businessBrandLabel || '经营品牌',
  agentBrands: options.agentBrands || '魏德米勒电联接国际贸易(上海)有限公司/威纶通科技有限公司(WEINVIEW)/威纶通科技有限公司(WEINVIEW)',
  businessBrands: options.businessBrands || '魏德米勒电联接国际贸易(上海)有限公司/威纶通科技有限公司(WEINVIEW)/威纶通科技有限公司(WEINVIEW)',
  tags: options.tags || [],
  stats: options.stats || createStats(),
  joinDate: options.joinDate || '026-05-26 10:45',
  expanded: options.expanded !== false,
  activeTab: options.activeTab || '基础信息',
  detailTabs: options.detailTabs || [...detailTabs],
  ribbon: options.ribbon || '',
  // 以下字段由真实接口使用；Mock 分类未传入时保持安全默认值。
  masked: options.masked === true,
  recommended: options.recommended === true,
  raw: options.raw || {}
})

const supplierTypeByModuleName = {
  厂家: 'factory',
  推荐供应商: 'recommended',
  本地代理商: 'localAgent',
  我的供应商: 'mine',
  全部供应商: 'all',
  我的客户: 'customer'
}

const categoryContents = {
  factory: {
    title: '',
    description: '',
    listTitle: '厂家列表'
  },
  recommended: {
    title: '',
    description: '',
    listTitle: '推荐供应商列表'
  },
  localAgent: {
    title: '',
    description: '',
    listTitle: '供应商列表'
  },
  mine: {
    title: '',
    description: '',
    listTitle: '我的供应商列表'
  },
  all: {
    title: '',
    description: '',
    listTitle: '全部供应商列表'
  },
  customer: {
    title: '',
    description: '',
    listTitle: '客户列表'
  }
}

const createSupplierGroups = () => {
  // 六个分类均已接入真实接口，初始为空，推荐数据只能来自后台标识。
  return {
    factory: { featured: [], standard: [] },
    recommended: { featured: [], standard: [] },
    // 本地代理商已接入真实接口，初始保持空列表，避免请求期间闪现 Mock 企业。
    localAgent: { featured: [], standard: [] },
    // “我的供应商”使用真实分页接口，不能在请求前展示 Mock 企业。
    mine: { featured: [], standard: [] },
    all: {
      featured: [],
      // “全部供应商”已经接入真实分页接口，不再保留可能误导用户的 Mock 企业。
      standard: []
    },
    customer: {
      featured: [],
      // “我的客户”使用本人邀请或绑定关系的真实分页数据，不能展示演示企业。
      standard: []
    }
  }
}

export default {
  name: 'FindSupplier',
  components: {
    SupplierCardContent
  },
  data() {
    return {
      filtersCollapsed: false,
      brandIntroVisible: false,
      brandDropdownVisible: false,
      brandSearchKeyword: '',
      brandSearchDirty: false,
      brandSortMode: 'full',
      brandGroupExpanded: {},
      seriesRequestId: 0,
      supplierRequestId: 0,
      supplierLoading: false,
      currentUserProvince: '',
      // IntersectionObserver 属于浏览器对象，仅用于观察列表底部，不参与页面渲染。
      supplierLoadObserver: null,
      supplierPage: {
        pageNo: 1,
        pageSize: 3,
        totalCount: 0,
        pageCount: 0
      },
      filters: {
        brandId: null,
        series: null,
        // 默认不限制区域：全部供应商首次查询应覆盖全部符合条件的数据。
        // 当前企业所在区域只由后端用于判断 VIP 的完整详情可见范围，不能作为前端默认筛选值。
        area: [],
        supplierType: '',
        keyword: '',
        startMonth: '',
        endMonth: '',
        companyType: '全部类型',
        filterBrand: '全部品牌',
        filterArea: '全部区域',
        relationship: '全部关系'
      },
      brandOptions: [],
      seriesOptions: [],
      areaOptions: handleTreeData(area, 'name', 'name'),
      areaCascaderProps: {
        expandTrigger: 'hover',
        checkStrictly: true
      },
      companyTypeOptions: ['全部类型', '厂家', '代理商', '经营', '无标识'],
      filterBrandOptions: ['全部品牌', '施耐德', '西门子', 'ABB', '魏德米勒'],
      // companyInfo/index 同源的公司类型树，仅供“我的客户”筛选使用。
      customerCompanyTypeTree: [],
      filterAreaOptions: ['全部区域', '四川省', '湖北省', '广东省'],
      relationshipOptions: ['全部关系', '已邀请', '已绑定', '无关系', '代理', '经营', '厂家', '认证', '自营', '无标识'],
      supplierTabs: [],
      categoryContents,
      supplierGroups: createSupplierGroups()
    }
  },
  created() {
    this.loadCurrentUserProvince()
    this.getBrandOptions()
    this.getCustomerCompanyTypes()
    this.getSupplierPanelConfig()
  },
  mounted() {
    // 页面挂载后观察列表底部标记，提前 240px 触发下一页，减少用户看到等待的时间。
    this.initSupplierLoadObserver()
  },
  beforeUnmount() {
    // 离开页面时主动断开观察，避免组件销毁后仍持有 DOM 引用。
    if (this.supplierLoadObserver) {
      this.supplierLoadObserver.disconnect()
      this.supplierLoadObserver = null
    }
  },
  computed: {
    currentCategory() {
      return this.categoryContents[this.filters.supplierType] || {}
    },
    currentSupplierGroup() {
      return this.supplierGroups[this.filters.supplierType] || { featured: [], standard: [] }
    },
    featuredSuppliers() {
      return this.currentSupplierGroup.featured
    },
    standardSuppliers() {
      return this.currentSupplierGroup.standard
    },
    allSuppliers() {
      return [...this.featuredSuppliers, ...this.standardSuppliers]
    },
    filteredFeaturedSuppliers() {
      return this.filterSupplierList(this.featuredSuppliers)
    },
    filteredStandardSuppliers() {
      return this.filterSupplierList(this.standardSuppliers)
    },
    isRegionalSupplierType() {
      return ['factory', 'recommended', 'localAgent'].includes(this.filters.supplierType)
    },
    /** 六个页签均按省分组；顶部区域和筛选控件仍按各分类原有规则工作。 */
    isRegionGroupedType() {
      return ['factory', 'recommended', 'localAgent', 'mine', 'all', 'customer'].includes(this.filters.supplierType)
    },
    /** 三个分类保留顶部品牌入口；区域筛选单独开放。 */
    showSecondaryBrandAreaFilter() {
      return !this.isRegionalSupplierType
    },
    activeFilterAreaOptions() {
      // 全国省份始终可查询，不依赖当前页数据，也不误用厂家的注册地址生成选项。
      return this.isRegionalSupplierType
        ? ['全部区域', ...area.map(item => item.name)]
        : this.filterAreaOptions
    },
    recommendedSupplierCount() {
      const first = this.filteredFeaturedSuppliers[0]
      const count = first && first.raw.regionSupplierCount
      return count === null || count === undefined ? null : Number(count)
    },
    standardRegionGroups() {
      if (!this.isRegionGroupedType) {
        return this.filteredStandardSuppliers.length
          ? [{ key: 'standard', title: this.currentCategory.listTitle, suppliers: this.filteredStandardSuppliers }]
          : []
      }
      // 后端已经完成全局分组排序；这里只合并跨页的同一分组，不重新改变卡片顺序。
      const groups = []
      const groupMap = new Map()
      this.filteredStandardSuppliers.forEach(supplier => {
        const province = supplier.raw.groupProvince || ''
        const isLocal = supplier.raw.regionGroupType === 'local'
        const key = `${isLocal ? 'local' : 'other'}:${province}`
        if (!groupMap.has(key)) {
          const group = {
            key,
            title: `区域 ${province || '未设置区域'}${isLocal ? '（本地区域）' : ''}`,
            totalCount: supplier.raw.regionSupplierCount == null ? null : Number(supplier.raw.regionSupplierCount),
            suppliers: []
          }
          groupMap.set(key, group)
          groups.push(group)
        }
        groupMap.get(key).suppliers.push(supplier)
      })
      return groups
    },
    /** 只有当前页小于后端返回的总页数时，才允许继续触发自动加载。 */
    hasMoreSupplierPages() {
      return ['all', 'factory', 'recommended', 'localAgent', 'mine', 'customer'].includes(this.filters.supplierType) &&
        this.supplierPage.pageCount > 0 &&
        this.supplierPage.pageNo < this.supplierPage.pageCount
    },
    /** 客户页只保留已确认的客户关系和业务阶段标签筛选项。 */
    activeRelationshipOptions() {
      return this.filters.supplierType === 'customer'
        ? ['全部关系', '邀请', '绑定', '无标识', '报价中', '合作中']
        : this.relationshipOptions
    },
    /** 客户页“全部类型”展示公司类型树的一级父类。 */
    activeCompanyTypeOptions() {
      if (this.filters.supplierType !== 'customer') return this.companyTypeOptions
      return ['全部类型'].concat(
        this.customerCompanyTypeTree.map(item => item.categoryName).filter(Boolean)
      )
    },
    /** 客户页“全部品牌”展示公司类型树的叶子节点，与公司信息页可选项一致。 */
    activeFilterBrandOptions() {
      if (this.filters.supplierType !== 'customer') return this.filterBrandOptions
      return ['全部品牌'].concat(
        this.flattenCustomerCompanyTypes(this.customerCompanyTypeTree)
          .map(item => item.categoryName)
          .filter(Boolean)
      )
    },
    selectedBrandInfo() {
      const brand = this.findBrandById(this.brandOptions, this.filters.brandId)
      return {
        simpleName: (brand && (brand.simpleName || brand.categoryName)) || '--',
        categoryName: brand && brand.categoryName ? brand.categoryName : '--',
        brandIntro: brand && brand.brandIntro ? brand.brandIntro : '暂无品牌介绍'
      }
    },
    /** 品牌简称和全称共用一份候选集，搜索时同时匹配两个名称。 */
    filteredBrandList() {
      const brands = this.brandOptions.reduce((result, group) => {
        return result.concat(group.children || [])
      }, [])
      if (!this.brandSearchDirty) return brands

      const keyword = this.brandSearchKeyword.trim().toLowerCase()
      if (!keyword) return brands
      return brands.filter(brand =>
        [brand.simpleName, brand.categoryName]
          .some(value => String(value || '').toLowerCase().includes(keyword))
      )
    },
    /** 推荐品牌固定置顶，其余品牌根据当前表头选择按拼音首字母或归属地区分组。 */
    brandDisplayGroups() {
      const recommended = []
      const grouped = {}
      this.filteredBrandList.forEach(brand => {
        if (String(brand.recommendFlag) === '1') {
          recommended.push(brand)
          return
        }

        const label = this.brandSortMode === 'full'
          ? this.resolveBrandInitialGroup(brand)
          : this.resolveBrandRegionGroup(brand)
        if (!grouped[label]) grouped[label] = []
        grouped[label].push(brand)
      })

      const compareBrand = (first, second) => {
        const field = this.brandSortMode === 'full' ? 'categoryName' : 'simpleName'
        return String(first[field] || '').localeCompare(String(second[field] || ''), 'zh-CN')
      }
      const groups = Object.keys(grouped)
        .sort((first, second) => this.compareBrandGroupLabel(first, second))
        .map(label => ({
          key: `${this.brandSortMode}-${label}`,
          label,
          children: grouped[label].slice().sort(compareBrand)
        }))

      if (recommended.length) {
        groups.unshift({
          key: `${this.brandSortMode}-recommended`,
          label: '推荐',
          children: recommended.slice().sort(compareBrand)
        })
      }
      return groups
    }
  },
  methods: {
    ...mapActions('supplierModule', [
      'customerDesktopList',
      'findSupplierPage',
      'findSupplierFilterOptions'
    ]),
    ...mapActions('purchaseMudule', [
      'listSkuCategoryForFirst',
      'listSkuCategoryForChildrenOne'
    ]),
    ...mapActions('myMessageModule', [
      'enterpriseTreeList',
      'getUser',
      'getEnterprise'
    ]),
    /** 与公司信息页复用同一来源；本省分组仍由后端登录企业独立判定。 */
    async loadCurrentUserProvince() {
      try {
        const userRes = await this.getUser()
        const userResponse = userRes && userRes.data
        if (!userResponse || String(userResponse.code) !== '0' || !userResponse.data || !userResponse.data.enterpriseId) return
        const res = await this.getEnterprise(userResponse.data.enterpriseId)
        const response = res && res.data
        if (response && String(response.code) === '0' && response.data) {
          this.currentUserProvince = response.data.province || ''
        }
      } catch (error) {
        this.currentUserProvince = ''
      }
    },
    /**
     * 初始化列表底部观察器。
     * root 使用浏览器可视区域，同时会自动考虑页面中间滚动容器的裁剪范围。
     */
    initSupplierLoadObserver() {
      if (typeof IntersectionObserver === 'undefined') return

      this.supplierLoadObserver = new IntersectionObserver(entries => {
        const triggerEntry = entries[0]
        if (triggerEntry && triggerEntry.isIntersecting) {
          this.loadNextSupplierPage()
        }
      }, {
        root: null,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0
      })

      if (this.$refs.supplierLoadTrigger) {
        this.supplierLoadObserver.observe(this.$refs.supplierLoadTrigger)
      }
    },
    async getSupplierPanelConfig() {
      try {
        const res = await this.customerDesktopList()
        const response = res.data && res.data.data && res.data.code === undefined
          ? res.data.data
          : res.data
        if (!response || String(response.code) !== '0' || !Array.isArray(response.data)) return

        const tabs = response.data
          .filter(item => item && supplierTypeByModuleName[item.moduleName])
          .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
          .map(item => ({
            label: item.moduleName,
            value: supplierTypeByModuleName[item.moduleName],
            brief: item.popupContent || '',
            introTitle: item.introTitle || '',
            introContent: item.introContent || ''
          }))

        if (!tabs.length) return

        this.supplierTabs = tabs
        tabs.forEach(item => {
          const category = this.categoryContents[item.value]
          if (!category) return
          category.title = item.introTitle || category.title
          category.description = item.introContent || category.description
        })
        if (!tabs.some(item => item.value === this.filters.supplierType)) {
          this.filters.supplierType = tabs[0].value
        }

        // 模块配置和品牌接口是并行加载的；两者任一后完成时都尝试触发真实列表查询。
        this.loadCurrentSupplierPage()
      } catch (error) {
        return null
      }
    },
    async getBrandOptions() {
      const res = await this.listSkuCategoryForFirst({
        pageNo: 1,
        pageSize: 99999
      })
      const { data, code } = res.data.data
      if (code !== '0' || !data.length) return

      const brandOptions = this.formatBrandOptions(data, 'categoryName')
      this.brandOptions = brandOptions

      // 二级品牌筛选使用真实品牌名称，避免继续展示与接口数据无关的固定 Mock 选项。
      this.filterBrandOptions = ['全部品牌'].concat(
        data.map(item => item.categoryName).filter(Boolean)
      )

      const firstBrand = brandOptions[0] && brandOptions[0].children[0]
      if (firstBrand) {
        this.setBrandIds(firstBrand.id)
        this.brandSearchKeyword = firstBrand.categoryName || ''
        this.getSeriesOptions(firstBrand.id)
        // 默认品牌确定后，若当前分类是“全部供应商”，立即加载第一页真实数据。
        this.loadCurrentSupplierPage()
        this.getSupplierFilterOptions(firstBrand.id)
      }
    },
    /** 复用 companyInfo/index 的企业类型树接口。 */
    async getCustomerCompanyTypes() {
      try {
        const res = await this.enterpriseTreeList({})
        const response = res && res.data
        if (!response || String(response.code) !== '0' || !Array.isArray(response.data)) return
        this.customerCompanyTypeTree = response.data
      } catch (error) {
        // 类型数据失败时仍保留“全部”筛选，不能阻塞客户列表。
        this.customerCompanyTypeTree = []
      }
    },
    /** 深度收集叶子公司类型，保持与公司信息页禁用父节点选择的规则一致。 */
    flattenCustomerCompanyTypes(nodes, result = []) {
      ;(nodes || []).forEach(node => {
        const children = Array.isArray(node.children) ? node.children : []
        if (children.length) {
          this.flattenCustomerCompanyTypes(children, result)
        } else if (node && node.id) {
          result.push(node)
        }
      })
      return result
    },
    /** 选择父级“全部类型”后，展开为它下面全部叶子公司类型 ID。 */
    resolveCustomerParentTypeIds() {
      if (this.filters.companyType === '全部类型') return []
      const parent = this.customerCompanyTypeTree.find(item => item.categoryName === this.filters.companyType)
      return parent ? this.flattenCustomerCompanyTypes([parent]).map(item => String(item.id)) : []
    },
    /** “全部品牌”实际选中的是叶子公司类型。 */
    resolveCustomerLeafTypeId() {
      if (this.filters.filterBrand === '全部品牌') return ''
      const leaf = this.flattenCustomerCompanyTypes(this.customerCompanyTypeTree)
        .find(item => item.categoryName === this.filters.filterBrand)
      return leaf && leaf.id ? String(leaf.id) : ''
    },
    clearBrandSelection() {
      // 客户页顶部品牌只是保留的页面公共控件，不改变筛选条件也不重新请求。
      if (this.filters.supplierType === 'customer') return
      this.filters.brandId = null
      this.brandSearchKeyword = ''
      this.brandSearchDirty = false
      // 清空顶部品牌也不联动全部供应商的系列、列表或下方筛选。
      if (this.filters.supplierType === 'all') return
      this.filters.series = null
      this.seriesOptions = []

      // 其他分类品牌为必填条件，清空时同步清空真实列表，不能继续展示上一个品牌的数据。
      this.clearAllSupplierResult()
    },
    setBrandIds(brandId) {
      this.filters.brandId = brandId
    },
    handleBrandInputFocus(event) {
      this.brandSearchDirty = false
      this.$nextTick(() => {
        if (event && event.target && typeof event.target.select === 'function') {
          event.target.select()
        }
      })
    },
    handleBrandDropdownHide() {
      this.brandIntroVisible = false
      this.brandSearchDirty = false
      const brand = this.findBrandById(this.brandOptions, this.filters.brandId)
      this.brandSearchKeyword = (brand && brand.categoryName) || ''
    },
    handleBrandSearchInput() {
      this.brandSearchDirty = true
      this.brandDropdownVisible = true
    },
    selectBrand(brand) {
      if (this.filters.supplierType === 'customer') {
        this.brandDropdownVisible = false
        return
      }
      this.brandSearchKeyword = brand.categoryName || ''
      this.brandSearchDirty = false
      this.brandDropdownVisible = false
      this.handleBrandChange(brand)
    },
    changeBrandSortMode(mode) {
      this.brandSortMode = mode
      this.brandGroupExpanded = {}
    },
    toggleBrandGroup(groupKey) {
      this.brandGroupExpanded[groupKey] = !this.isBrandGroupExpanded(groupKey)
    },
    isBrandGroupExpanded(groupKey) {
      return this.brandGroupExpanded[groupKey] !== false
    },
    resolveBrandInitialGroup(brand) {
      const initial = String(brand.initialCode || '').trim().charAt(0).toUpperCase()
      return /^[A-Z]$/.test(initial) || /^\d$/.test(initial) ? initial : '#'
    },
    resolveBrandRegionGroup(brand) {
      const region = String(brand.countryRegion || '').trim()
      // 国内品牌保存的是“省 市 区”路径，只取省份；国外品牌保存的是国家名称，直接作为分组。
      return region ? region.split(/\s+/)[0] : '其他'
    },
    compareBrandGroupLabel(first, second) {
      if (first === '#') return 1
      if (second === '#') return -1
      return first.localeCompare(second, 'zh-CN', { numeric: true })
    },
    formatBrandOptions(data, field) {
      const options = getLetterSortData(JSON.parse(JSON.stringify(data)), 'initialCode')
      options.forEach((group, index) => {
        group.id = `a_${index + 1}`
        group.label = group.letter
        if (group.children) {
          group.children.forEach(brand => {
            brand.simpleName = brand.simpleName || brand.categoryName
            brand.label = brand[field] || brand.categoryName
            delete brand.children
          })
        }
      })
      return options
    },
    findBrandById(options, brandId) {
      for (const group of options) {
        const brand = (group.children || []).find(item => String(item.id) === String(brandId))
        if (brand) return brand
      }
      return null
    },
    openBrandLink(field) {
      const brand = this.findBrandById(this.brandOptions, this.filters.brandId)
      const url = brand && brand[field]
      if (!url) {
        this.$message({
          message: '暂无数据',
          type: 'warning'
        })
        return
      }
      window.open(url, '_blank')
    },
    async getSeriesOptions(brandId) {
      const requestId = ++this.seriesRequestId
      const data = await this.getSeriesChildren(brandId)
      if (requestId !== this.seriesRequestId) return

      this.seriesOptions = this.formatLazySeriesOptions(data)
      this.filters.series = null
    },
    async loadSeriesOptions({ action, parentNode, callback }) {
      if (action !== 'LOAD_CHILDREN_OPTIONS') {
        callback()
        return
      }

      try {
        const data = await this.getSeriesChildren(parentNode.id)
        parentNode.children = this.formatLazySeriesOptions(data)
        callback()
      } catch (error) {
        callback(error)
      }
    },
    async getSeriesChildren(parentId) {
      const res = await this.listSkuCategoryForChildrenOne({ parentId })
      const response = res.data.code !== undefined ? res.data : res.data.data
      if (String(response.code) !== '0') return []

      return Array.isArray(response.data)
        ? response.data
        : (response.data && Array.isArray(response.data.data) ? response.data.data : [])
    },
    formatLazySeriesOptions(data) {
      return data.map(item => {
        const option = {
          ...item,
          label: item.categoryName
        }
        if (item.hasChildren) {
          option.children = null
        } else {
          delete option.children
        }
        return option
      })
    },
    filterSupplierList(suppliers) {
      // 已接入的分类由后端按关键字筛选和分页，不能只过滤当前页造成总数与列表不一致。
      if (['all', 'factory', 'recommended', 'localAgent', 'mine', 'customer'].includes(this.filters.supplierType)) return suppliers

      // 其余未接入分类暂时保留原有 Mock 列表的前端关键字过滤。
      const keyword = this.filters.keyword.trim().toLowerCase()
      if (!keyword) return suppliers
      return suppliers.filter(item =>
        [item.name, item.fullName, item.address, item.contact, item.phone]
          .some(value => String(value).toLowerCase().includes(keyword))
      )
    },
    changeSupplierType(type) {
      // 切换分类时先更新当前类型，公共接口根据 supplierType 单独获取每一种数据。
      this.filters.supplierType = type
      if (type === 'all') this.getSupplierFilterOptions(this.resolveRequestBrandId())
      if (type === 'customer') {
        if (!this.activeCompanyTypeOptions.includes(this.filters.companyType)) {
          this.filters.companyType = '全部类型'
        }
        if (!this.activeFilterBrandOptions.includes(this.filters.filterBrand)) {
          this.filters.filterBrand = '全部品牌'
        }
      }
      if (!this.activeRelationshipOptions.includes(this.filters.relationship)) {
        this.filters.relationship = '全部关系'
      }
      this.filtersCollapsed = this.supplierGroups[type].featured
        .concat(this.supplierGroups[type].standard)
        .every(item => !item.expanded)

      // 已接入分类均从第一页按当前筛选条件刷新真实数据。
      if (['all', 'factory', 'recommended', 'localAgent', 'mine', 'customer'].includes(type)) {
        this.supplierPage.pageNo = 1
        this.loadSupplierPage(type)
      }
    },
    handleBrandChange(node) {
      if (this.filters.supplierType === 'customer') return
      this.brandIntroVisible = false
      this.setBrandIds(node.id)
      // 全部供应商只更新顶部选择的展示值；必须在系列清空和系列接口调用之前返回。
      // 下方品牌、页码、已展开卡片及现有列表均保持原状，不触发任何业务请求。
      if (this.filters.supplierType === 'all') return
      this.filters.series = null
      this.seriesOptions = []
      this.getSeriesOptions(node.id)

      // 品牌驱动分类变更后回到第一页重新请求。
      this.supplierPage.pageNo = 1
      this.loadCurrentSupplierPage()
      this.getSupplierFilterOptions(node.id)
    },
    handleSeriesChange(node) {
      if (['all', 'customer'].includes(this.filters.supplierType)) return
      // 系列ID由后端按经营品牌过滤；选择后重新查询第一页。
      this.filters.series = node.id
      this.applyFilters()
    },
    toggleSupplier(supplier) {
      supplier.expanded = !supplier.expanded
      this.filtersCollapsed = this.allSuppliers.every(item => !item.expanded)
    },
    toggleAllSuppliers() {
      const shouldExpand = this.filtersCollapsed
      this.allSuppliers.forEach(item => {
        item.expanded = shouldExpand
      })
      this.filtersCollapsed = !shouldExpand
    },
    selectSupplierTab(supplier, tab) {
      supplier.activeTab = tab
    },
    applyFilters() {
      // 后端筛选条件发生变化时统一回到第一页，防止新条件的总页数小于旧页码。
      this.supplierPage.pageNo = 1
      this.loadCurrentSupplierPage()
    },
    /** 客户页顶部区域仅保留展示，不改变列表数据。 */
    handleTopAreaChange() {
      if (this.filters.supplierType === 'customer') return
      this.applyFilters()
    },
    /** 二级品牌改变候选集后，同步刷新完整候选集的类型、区域和关系选项。 */
    handleFilterBrandChange() {
      this.supplierPage.pageNo = 1
      this.loadCurrentSupplierPage()
      // 客户页“全部品牌”来自公司类型树，不调用仅适用于供应商品牌的 filter-options。
      if (this.filters.supplierType === 'customer') return
      this.getSupplierFilterOptions(this.resolveRequestBrandId())
    },
    resetFilters() {
      // 重置二级筛选，但保留顶部当前品牌、系列和区域选择，符合页面现有交互范围。
      this.filters.keyword = ''
      this.filters.startMonth = ''
      this.filters.endMonth = ''
      this.filters.companyType = '全部类型'
      this.filters.filterBrand = '全部品牌'
      this.filters.filterArea = '全部区域'
      this.filters.relationship = '全部关系'

      // 重置完成后立即使用干净条件查询第一页真实数据。
      this.applyFilters()
    },

    /**
     * 当前分类需要真实接口时发起查询。
     * 其余未接入分类仍使用现有页面数据，不发送无意义请求。
     */
    loadCurrentSupplierPage() {
      if (!['all', 'factory', 'recommended', 'localAgent', 'mine', 'customer'].includes(this.filters.supplierType)) return
      this.loadSupplierPage(this.filters.supplierType)
    },

    /**
     * 从后端完整候选集读取下拉项，不能仅从当前分页已加载的卡片中汇总。
     * 当前页默认只有十条，前端汇总会遗漏后续页面的企业类型、品牌或区域。
     */
    async getSupplierFilterOptions(brandId = this.resolveRequestBrandId()) {
      // 初始化可能显式传入顶部首个品牌；全部供应商必须重新取下方选择，避免筛选项仍被顶部品牌限制。
      if (this.filters.supplierType === 'all') brandId = this.resolveRequestBrandId()
      // 该接口固定按 supplierType=all 读取供应商品牌候选集；客户页不应调用。
      if (this.filters.supplierType === 'customer') return
      // 全部供应商允许不传品牌，以便默认加载平台全部供应商的筛选项。
      if (!brandId && this.filters.supplierType !== 'all') return

      try {
        const query = this.buildSupplierQuery(brandId)
        query.supplierType = 'all'
        const res = await this.findSupplierFilterOptions(query)
        const response = res && res.data
        if (!response || String(response.code) !== '0' || !Array.isArray(response.data)) return

        const grouped = response.data.reduce((result, option) => {
          if (option && option.optionType && option.optionValue) {
            result[option.optionType].push(option.optionValue)
          }
          return result
        }, { companyType: [], brand: [], area: [], relationship: [] })
        const unique = values => [...new Set(values)]
        // “全部类型”是页面固定的企业标签入口，不从后端公司分类候选集覆盖。
        this.companyTypeOptions = ['全部类型', '厂家', '代理商', '经营', '无标识']
        this.filterBrandOptions = ['全部品牌'].concat(unique(grouped.brand))
        this.filterAreaOptions = ['全部区域'].concat(unique(grouped.area))
        // “全部关系”保留原有关系及标签筛选项。
        this.relationshipOptions = ['全部关系', '已邀请', '已绑定', '无关系', '代理', '经营', '厂家', '认证', '自营', '无标识']

        if (!this.companyTypeOptions.includes(this.filters.companyType)) this.filters.companyType = '全部类型'
        if (!this.filterBrandOptions.includes(this.filters.filterBrand)) this.filters.filterBrand = '全部品牌'
        if (!this.activeFilterAreaOptions.includes(this.filters.filterArea)) this.filters.filterArea = '全部区域'
        if (!this.relationshipOptions.includes(this.filters.relationship)) this.filters.relationship = '全部关系'
      } catch (error) {
        // 下拉项加载失败不阻塞分页结果；保留当前已加载选项和默认“全部”入口。
        return null
      }
    },

    /**
     * 调用公共分页接口加载已接入的供应商分类。
     * 请求序号用于丢弃较早返回的响应，避免快速切换品牌时旧数据覆盖新数据。
     */
    async loadSupplierPage(supplierType = this.filters.supplierType, append = false) {
      // 全部供应商、我的供应商均由下方品牌筛选；我的客户不使用品牌。
      const brandId = ['all', 'mine'].includes(supplierType)
        ? (this.filters.filterBrand === '全部品牌' ? '' : this.resolveRequestBrandId())
        : (supplierType === 'customer' ? '' : (supplierType === 'factory' ? this.filters.brandId : this.resolveRequestBrandId()))
      if (!brandId && !['all', 'mine', 'customer'].includes(supplierType)) {
        // 其他供应商分类仍要求品牌必填；未选择品牌时不发送必然失败的请求。
        this.clearAllSupplierResult()
        return
      }

      const requestId = ++this.supplierRequestId
      // 记住本次请求页码，追加失败时需要回退，否则下次滚动会跳过该页。
      const requestedPageNo = this.supplierPage.pageNo
      // 只有请求成功且已合并数据时，才继续检查是否需要自动加载，避免失败后循环重试。
      let loadSucceeded = false
      this.supplierLoading = true
      try {
        // 所有筛选值在一个方法中转换，页面字段和后端公共协议保持单一映射入口。
        const query = this.buildSupplierQuery(brandId, supplierType)
        const res = await this.findSupplierPage(query)
        if (requestId !== this.supplierRequestId) return

        const pageData = this.extractSupplierPageData(res)
        if (!pageData) {
          // 业务错误已由全局响应拦截器提示。首页失败时清空旧条件的数据；
          // 追加页失败时则保留前面已加载的卡片，并回退页码以便后续重试。
          if (append) {
            this.supplierPage.pageNo = Math.max(1, requestedPageNo - 1)
          } else {
            this.clearAllSupplierResult(false)
          }
          return
        }

        const rows = Array.isArray(pageData.rows) ? pageData.rows : []
        const suppliers = rows.map((row, index) => this.mapSupplierRecord(row, index, append))

        // 推荐供应商使用页面现有红色推荐卡片，其余数据进入普通列表；两组内部保持后端排序。
        const nextFeatured = suppliers.filter(item => item.recommended)
        const nextStandard = suppliers.filter(item => !item.recommended)
        this.supplierGroups[supplierType] = {
          // 首页或条件变更时替换列表；滚动加载时在已有数据后追加，不让上一页卡片消失。
          featured: append ? this.supplierGroups[supplierType].featured.concat(nextFeatured) : nextFeatured,
          standard: append ? this.supplierGroups[supplierType].standard.concat(nextStandard) : nextStandard
        }
        this.supplierPage.totalCount = Number(pageData.totalCount || 0)
        this.supplierPage.pageCount = Number(pageData.pageCount || 0)
        this.filtersCollapsed = this.allSuppliers.length > 0 && this.allSuppliers.every(item => !item.expanded)
        loadSucceeded = true
      } catch (error) {
        if (requestId !== this.supplierRequestId) return
        if (append) {
          // 追加页失败时回到上一页，保留已加载列表，用户再次滚动时可重试同一页。
          this.supplierPage.pageNo = Math.max(1, requestedPageNo - 1)
        } else {
          this.clearAllSupplierResult(false)
        }
        this.$message.error('供应商列表加载失败，请稍后重试')
      } finally {
        // 只有最后一次请求有权关闭加载状态，防止并发请求造成加载动画提前消失。
        if (requestId === this.supplierRequestId) {
          this.supplierLoading = false
          // 当首页数据不足以填满可视区域时，观察器可能始终处于相交状态。
          // 主动再检查一次底部位置，保证能连续补足下一页而不需要手动点击。
          if (loadSucceeded && supplierType === this.filters.supplierType) {
            this.$nextTick(this.loadNextSupplierPageIfVisible)
          }
        }
      }
    },

    /** 底部标记进入预加载区域时，串行追加下一页。 */
    loadNextSupplierPage() {
      if (this.supplierLoading || !this.hasMoreSupplierPages) return
      this.supplierPage.pageNo += 1
      this.loadSupplierPage(this.filters.supplierType, true)
    },

    /** 检查底部标记是否仍在预加载范围内，用于首页内容过少时自动继续翻页。 */
    loadNextSupplierPageIfVisible() {
      const trigger = this.$refs.supplierLoadTrigger
      if (!trigger || this.supplierLoading || !this.hasMoreSupplierPages) return

      const triggerRect = trigger.getBoundingClientRect()
      if (triggerRect.top <= window.innerHeight + 240 && triggerRect.bottom >= 0) {
        this.loadNextSupplierPage()
      }
    },

    /** 将页面筛选模型转换为后端 FindSupplierPageQueryVo。 */
    buildSupplierQuery(brandId, supplierType = this.filters.supplierType) {
      const isCustomer = supplierType === 'customer'
      const areaFilter = isCustomer ? { name: '', level: 'country' } : this.resolveAreaFilter(supplierType)
      const relationMap = {
        已邀请: 'invited',
        已绑定: 'bound',
        无关系: 'none',
        代理: 'agent',
        经营: 'business',
        厂家: 'manufacturer',
        认证: 'auth',
        自营: 'proprietary',
        无标识: 'unmarked'
      }
      const companyTypeTagMap = {
        厂家: 'manufacturer',
        代理商: 'agent',
        经营: 'business',
        无标识: 'unmarked'
      }
      const customerRelationMap = {
        邀请: 'invited',
        绑定: 'bound',
        无标识: 'none'
      }
      const customerStageMap = {
        报价中: 'quoting',
        合作中: 'cooperating'
      }
      const isRegional = ['factory', 'recommended', 'localAgent'].includes(supplierType)
      const tagType = isCustomer
        ? (customerStageMap[this.filters.relationship] || '')
        : ((isRegional ? '' : companyTypeTagMap[this.filters.companyType]) || relationMap[this.filters.relationship] || '')

      return {
        supplierType,
        brandId: isCustomer ? '' : (brandId ? String(brandId) : ''),
        // 二级品牌覆盖顶部品牌时，原系列不再属于当前请求品牌，必须清空系列条件。
        // 全部供应商和我的供应商不受顶部品牌限制，因此顶部品牌下的系列也不参与查询。
        seriesId: !['all', 'mine', 'customer'].includes(supplierType) && String(brandId) === String(this.filters.brandId)
          ? (this.filters.series || '') : '',
        areaCode: areaFilter.name,
        areaLevel: areaFilter.level,
        keyword: this.filters.keyword.trim(),
        startMonth: this.filters.startMonth || '',
        endMonth: this.filters.endMonth || '',
        // 客户页的“全部品牌”传叶子公司类型；“全部类型”传对应父级下全部叶子类型。
        companyType: isCustomer ? this.resolveCustomerLeafTypeId() : '',
        companyTagType: isRegional ? (companyTypeTagMap[this.filters.companyType] || '') : '',
        companyTypeIds: isCustomer ? this.resolveCustomerParentTypeIds() : [],
        brandRelationType: tagType === 'agent' || tagType === 'business' ? tagType : '',
        relationshipType: isCustomer
          ? (customerRelationMap[this.filters.relationship] || '')
          : (['invited', 'bound', 'none'].includes(tagType) ? tagType : ''),
        tagType,
        pageNo: this.supplierPage.pageNo,
        pageSize: this.supplierPage.pageSize
      }
    },

    /**
     * 解析实际请求品牌。
     * 全部供应商的二级品牌未选择时返回空值；品牌驱动分类继续使用顶部品牌。
     * 选择二级品牌时按真实品牌名称找到对应ID，不能在未匹配时回退到顶部品牌。
     */
    resolveRequestBrandId() {
      if (this.isRegionalSupplierType) return this.filters.brandId
      // 全部供应商只使用下方品牌选择；默认全部品牌与顶部品牌、系列完全解耦。
      if (this.filters.filterBrand === '全部品牌') return this.filters.supplierType === 'all' ? '' : this.filters.brandId

      for (const group of this.brandOptions) {
        const brand = (group.children || []).find(item =>
          item.categoryName === this.filters.filterBrand || item.simpleName === this.filters.filterBrand
        )
        if (brand) return brand.id
      }
      return this.filters.supplierType === 'all' ? '' : this.filters.brandId
    },

    /** 根据级联路径或二级省份筛选，生成后端要求的中文区域名称和层级。 */
    resolveAreaFilter(supplierType = this.filters.supplierType) {
      if (['factory', 'recommended', 'localAgent'].includes(supplierType)) {
        // 顶部只显示个人本省；查询范围完全由工具栏控制，默认全国。
        return this.filters.filterArea === '全部区域'
          ? { name: '', level: 'country' }
          : { name: this.filters.filterArea, level: 'province' }
      }
      const areaPath = Array.isArray(this.filters.area) ? this.filters.area : []
      if (this.filters.filterArea !== '全部区域') {
        return { name: this.filters.filterArea, level: 'province' }
      }

      const levelMap = { 1: 'province', 2: 'city', 3: 'district' }
      return {
        name: areaPath.length ? areaPath[areaPath.length - 1] : '',
        level: levelMap[areaPath.length] || 'country'
      }
    },

    /** 兼容项目统一响应包装，取出 PageOut 数据。 */
    extractSupplierPageData(res) {
      const response = res && res.data
      if (!response || String(response.code) !== '0') return null

      const data = response.data
      if (data && Array.isArray(data.rows)) return data
      if (data && data.data && Array.isArray(data.data.rows)) return data.data
      return null
    },

    /** 把后端公共 VO 转换为 SupplierCardContent 已有的展示模型。 */
    mapSupplierRecord(row, index, append = false) {
      const masked = Number(row.maskedFlag) === 1
      const isCustomer = this.filters.supplierType === 'customer'
      const agentBrandNames = this.joinBrandNames(row.agentBrands)
      const businessBrandNames = this.joinBrandNames(row.businessBrands)

      // 供应商卡片使用后端的企业＋推荐省份组合ID，防止同企业不同省份的展开状态相互覆盖。
      // 联系人由该卡片的 raw.userList 完整展示，不再依赖代表用户ID；我的客户仍保留企业维度。
      const cardId = this.filters.supplierType === 'customer'
        ? row.enterpriseId || row.supplierId
        : row.supplierId || row.userId
      return createSupplier(cardId || `supplier-${index}`, {
        // 脱敏企业名称由后端保留前两位，前端只做空值兜底，不能覆盖该安全展示结果。
        name: row.supplierName || (masked ? '******' : (row.fullName || '--')),
        fullName: row.fullName || (masked ? '******' : (row.supplierName || '--')),
        code: masked ? '******' : (row.enterpriseCode || '--'),
        address: masked ? '******' : this.formatSupplierAddress(row),
        contact: masked ? '******' : (row.legalPersonName || '--'),
        phone: masked ? '******' : (row.legalPersonPhone || '--'),
        types: masked ? '******' : (row.companyTypeName || row.companyType || '--'),
        agentBrands: masked ? '******' : (agentBrandNames || '暂无'),
        businessBrands: masked ? '******' : (businessBrandNames || '暂无'),
        // 脱敏企业不返回业务汇总，不能以 0 值伪装或泄露真实经营规模。
        stats: masked ? [] : this.buildSupplierStats(row),
        joinDate: masked ? '******' : (row.joinTime || '--'),
        tags: this.buildSupplierTags(row, masked),
        detailTabs: isCustomer ? [...customerDetailTabs] : [...detailTabs],
        isCustomer,
        purchasePreferences: row.purchasePreferences || '暂无',
        ribbon: Number(row.recommendedFlag) === 1 ? '推荐供应商' : '',
        // 仅首页第一家默认展开；追加页保持收起，避免每页都出现一张自动展开的长卡片。
        expanded: !append && index === 0,
        masked,
        recommended: Number(row.recommendedFlag) === 1,
        raw: row
      })
    },

    /** 将品牌详情数组转换成卡片当前使用的斜杠分隔文本，并自动去重。 */
    joinBrandNames(brands) {
      if (!Array.isArray(brands)) return ''
      return [...new Set(brands.map(item => item && item.brandName).filter(Boolean))].join('/')
    },

    /** 将后端九项业务汇总转换为卡片头部的固定展示顺序。 */
    buildSupplierStats(row) {
      const statistics = row.statistics || {}
      return [
        { label: '供应商', value: this.statisticsValue(statistics.supplierNums) },
        { label: '客户', value: this.statisticsValue(statistics.customerNums) },
        { label: '代理品牌', value: this.statisticsValue(statistics.agentBrandNums) },
        { label: '自动接单', value: this.statisticsValue(statistics.autoQuoteNums) },
        { label: '报价单', value: this.statisticsValue(statistics.quoteNums) },
        { label: '销售订单', value: this.statisticsValue(statistics.saleNums) },
        { label: '询价单', value: this.statisticsValue(statistics.inquiryNums) },
        { label: '采购订单', value: this.statisticsValue(statistics.purchaseNums) },
        { label: '经营品牌', value: this.statisticsValue(statistics.businessBrandNums) }
      ]
    },

    /** 后端统计字段缺失时按 0 展示，避免卡片出现 Mock 值或空白。 */
    statisticsValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },

    /** 拼接省、市、区和详细地址，避免详细地址已经包含行政区时重复展示。 */
    formatSupplierAddress(row) {
      const region = [row.province, row.city, row.district].filter(Boolean).join('')
      const address = row.address || row.enterpriseRegisteredAddress || ''
      if (!address) return region || '--'
      if (region && address.indexOf(region) !== 0) return `${region}${address}`
      return address
    },

    /** 根据后端关系和企业标识生成页面标签；脱敏记录不暴露代理、经营等业务标签。 */
    buildSupplierTags(row, masked) {
      if (masked) return [{ text: '信息已脱敏', type: 'gray' }]

      if (this.filters.supplierType === 'customer') {
        const tags = [{ text: '我的客户', type: 'green' }]
        if (row.relationshipType === 'invited') tags.push({ text: '邀请', type: 'green' })
        else if (row.relationshipType === 'bound') tags.push({ text: '绑定', type: 'yellow' })
        else tags.push({ text: '无标识', type: 'gray' })

        if (row.customerStage === 'cooperating') tags.push({ text: '合作中', type: 'blue' })
        else if (row.customerStage === 'quoting') tags.push({ text: '报价中', type: 'orange' })
        return tags
      }

      const tags = []
      if (row.relationshipType === 'invited') tags.push({ text: '邀请', type: 'green' })
      if (row.relationshipType === 'bound') tags.push({ text: '绑定', type: 'yellow' })
      if (Number(row.agencyFlag) === 1) tags.push({ text: '代理', type: 'red' })
      if (Number(row.businessBrandFlag) === 1) tags.push({ text: '经营', type: 'cyan' })
      if (Number(row.manufacturerFlag) === 1) tags.push({ text: '厂家', type: 'purple' })
      if (Number(row.authFlag) === 1) tags.push({ text: '认证', type: 'outline-red' })
      if (String(row.proprietaryFlag) === '1') tags.push({ text: '自营', type: 'blue' })
      return tags.length ? tags : [{ text: '无标识', type: 'gray' }]
    },

    /** 清空已接入分类的真实列表和分页统计。 */
    clearAllSupplierResult(invalidateRequest = true) {
      if (invalidateRequest) {
        // 主动清空时递增序号，使此前尚未返回的请求自动失效。
        this.supplierRequestId += 1
      }
      this.supplierGroups.all = { featured: [], standard: [] }
      this.supplierGroups.factory = { featured: [], standard: [] }
      this.supplierGroups.recommended = { featured: [], standard: [] }
      this.supplierGroups.localAgent = { featured: [], standard: [] }
      this.supplierGroups.mine = { featured: [], standard: [] }
      this.supplierGroups.customer = { featured: [], standard: [] }
      this.supplierPage.totalCount = 0
      this.supplierPage.pageCount = 0
      this.supplierLoading = false
    }
  }
}
</script>

<style lang="less">
.find-supplier-page {
  min-width: 1120px;
  padding-bottom: 28px;
  color: #4d5155;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.brand-filter {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 18px;

  &__fields {
    display: flex;
    align-items: flex-start;
    flex: 1;
    gap: 20px;
  }

  &__links {
    display: flex;
    align-items: center;
    gap: 22px;
    padding-top: 9px;
    color: #00a9a2;
    font-weight: 600;
    white-space: nowrap;

    span {
      cursor: pointer;
    }

    .notice {
      color: #ff6b00;

      i {
        margin-right: 5px;
        padding: 1px 4px;
        border-radius: 2px;
        color: #fff;
        background: #ff6b00;
        font-size: 10px;
        font-style: normal;
        font-weight: 600;
      }

      b {
        margin-right: 2px;
      }
    }
  }
}

.filter-field {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;

  .field-label {
    margin-right: 10px;
    color: #a3a6a8;
    white-space: nowrap;
  }

  &--brand {
    width: 390px;

    .brand-input-shell {
      width: 320px;
    }
  }

  &--series {
    width: 260px;

    .vue-treeselect {
      width: 215px;
    }
  }

  &--area {
    flex: 0 0 auto;

    .area-input-shell {
      flex: 0 0 215px;
      width: 215px;
      height: 30px !important;
      overflow: hidden;
      border-radius: 4px;
      box-sizing: border-box;
      background: #fff;
      border: 1px solid #dcdfe6 !important;

      &:hover,
      &:focus-within {
        border-color: #aebbc2 !important;
      }
    }
  }

  .vip-tip {
    margin-left: 10px;
    color: #babdc0;
    white-space: nowrap;
  }
}

// 顶部品牌、区域由 shell 统一绘制边框，内部输入框不再叠加公共边框。
.find-supplier-page .brand-filter {
  .brand-input-shell .el-input__inner,
  .area-input-shell .el-input__inner {
    border: none !important;
    border-radius: 0;
    box-shadow: none !important;
  }
}

.area-selector.el-cascader {
  display: block;
  width: 100%;
  height: 100%;
  line-height: 28px;

  .el-input__wrapper {
    height: 28px !important;
    min-height: 28px !important;
    padding: 0 10px;
    border: 0 !important;
    border-radius: 0;
    background: transparent !important;
    box-shadow: none !important;
  }

  .el-input__inner {
    height: 28px;
    padding-left: 8px !important;
    color: #606266;
    font-size: 12px;
    line-height: 28px;
  }

}

.brand-select-tooltip-trigger {
  width: inherit;
}

.find-supplier-page .local-area-input {
  display: block;
  width: 100%;
  height: 100%;

  .el-input__wrapper {
    height: 28px !important;
    min-height: 28px !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0;
    background: transparent !important;
    box-shadow: none !important;
  }

  .el-input__inner {
    height: 28px;
    padding: 0 10px !important;
    box-sizing: border-box;
    color: #606266;
    font-size: 12px;
    line-height: 28px;
  }
}

.brand-input-shell {
  height: 30px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-sizing: border-box;
  background: #fff;

  &:hover,
  &:focus-within {
    border-color: #aebbc2;
  }

  .brand-picker-input {
    display: block;
    width: 100%;
    height: 100%;
  }

  .el-input__wrapper {
    height: 28px !important;
    min-height: 28px !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0;
    background: transparent !important;
    box-shadow: none !important;
  }

  .el-input__inner {
    height: 28px;
    padding: 0 10px !important;
    box-sizing: border-box;
    color: #606266;
    font-size: 12px;
    line-height: 28px;
  }
}

.brand-picker-popper.el-popover {
  padding: 0 !important;
  overflow: hidden;
  border-radius: 8px;
}

.brand-picker {
  color: #555b5e;
  font-size: 13px;

  &__header,
  &__row {
    display: grid;
    grid-template-columns: 220px minmax(280px, 1fr) 120px;
    align-items: center;
  }

  &__header {
    height: 42px;
    padding: 0 14px;
    border-bottom: 1px solid #e8ecef;
    box-sizing: border-box;
    color: #2f3437;
    font-weight: 600;

    button {
      width: max-content;
      padding: 0;
      border: 0;
      outline: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      font: inherit;
    }

    button.active {
      color: #00a9a2;
    }

    .sort-arrow {
      margin-left: 5px;
      color: #b6bdc1;
      font-size: 15px;
    }

    button.active .sort-arrow {
      color: #00a9a2;
    }
  }

  &__body {
    max-height: 360px;
    overflow-y: auto;
    padding: 4px 0 8px;
  }

  &__group-title,
  &__row {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    text-align: left;
  }

  &__group-title {
    display: flex;
    height: 34px;
    padding: 0 14px;
    align-items: center;
    color: #5e6467;
    cursor: pointer;

    .group-arrow {
      width: 18px;
      color: #767d80;
      font-size: 10px;
    }

    b {
      margin-left: 10px;
      color: #f23b3b;
    }
  }

  &__row {
    min-height: 34px;
    padding: 6px 14px 6px 32px;
    box-sizing: border-box;
    color: #5f6568;
    cursor: pointer;
    font-size: 13px;

    &:hover,
    &.selected {
      background: #edf8f7;
      color: #008f89;
    }

    strong,
    > span {
      overflow: hidden;
      padding-right: 16px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      color: #252a2d;
    }
  }

  &__sku-num {
    padding-right: 0 !important;
    color: #303538;
    font-weight: 600;
    text-align: right;
  }

  &__empty {
    padding: 36px 0;
    color: #a6abad;
    text-align: center;
  }
}

.brand-intro-tooltip {
  max-width: 360px;
  line-height: 1.6;
  white-space: normal;

  &__content {
    p {
      margin: 0;
    }

    span {
      color: #d9d9d9;
    }
  }
}

.find-supplier-page .el-input__wrapper {
  box-shadow: 0 0 0 1px #d8dcdf inset;
}

.supplier-panel {
  height: 172px;
  margin: 0 18px 18px;
  padding: 0 18px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #fff;
}

.supplier-tabs {
  display: flex;
  height: 48px;
  align-items: stretch;
  border-bottom: 1px solid #d9e2e8;

  button {
    position: relative;
    min-width: 112px;
    padding: 0 14px;
    border: 0;
    outline: none;
    background: transparent;
    color: #676b6e;
    cursor: pointer;
    font-size: 14px;

    &.active {
      color: #00a8a2;
      font-weight: 600;

      &::after {
        position: absolute;
        right: 10px;
        bottom: -1px;
        left: 10px;
        height: 2px;
        background: #00aaa4;
        content: "";
      }
    }

    &.factory-entry {
      color: #48a09c;
    }
  }
}

.benefit-banner {
  position: relative;
  display: flex;
  height: 88px;
  margin-top: 18px;
  padding: 12px 24px;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  background-color: #eefaf8 !important;
  background-image: linear-gradient(105deg, #e3faf7 0%, #d5f7f3 52%, #edfafa 100%) !important;

  &::before,
  &::after {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    content: "";
  }

  &::before {
    bottom: -128px;
    left: -70px;
    width: 182px;
    height: 182px;
    background: rgba(157, 231, 228, 0.38);
  }

  &::after {
    top: -260px;
    left: 28%;
    width: 324px;
    height: 324px;
    background: rgba(157, 231, 228, 0.18);
  }

  h3 {
    position: relative;
    z-index: 2;
    margin: 0 0 4px;
    color: #08aaa4;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    position: relative;
    z-index: 2;
    margin: 0;
    color: #454b4e;
    font-size: 12px;
    line-height: 18px;
  }

  .banner-pattern {
    position: absolute;
    top: -54px;
    right: -45px;
    width: 198px;
    height: 198px;
    border: 1px dashed rgba(72, 160, 156, 0.38);
    border-radius: 50%;
    box-shadow:
      0 0 0 10px rgba(255, 255, 255, 0.48),
      0 0 0 20px rgba(157, 231, 228, 0.2),
      0 0 0 31px rgba(255, 255, 255, 0.25);

    &::before {
      position: absolute;
      top: 61px;
      left: 61px;
      width: 74px;
      height: 74px;
      border-radius: 50%;
      background: rgba(157, 231, 228, 0.28);
      content: "";
    }
  }
}

.search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 18px 16px;
  padding: 14px 16px;
  border: 1px solid #e5e9ec;
  border-radius: 8px;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.04);

  > .el-input,
  > .el-select {
    height: 34px;
  }

  .el-input__wrapper,
  .el-select__wrapper {
    height: 34px !important;
    min-height: 34px !important;
    padding: 1px 11px;
    border-radius: 5px;
    box-sizing: border-box;
    background: #f8fafb;
    box-shadow: 0 0 0 1px #dce2e6 inset !important;
    transition: box-shadow 0.2s, background-color 0.2s;

    &:hover {
      background: #fff;
      box-shadow: 0 0 0 1px #aebbc2 inset !important;
    }

    &.is-focus {
      background: #fff;
      box-shadow: 0 0 0 1px #00aaa4 inset !important;
    }
  }

  .el-input__inner,
  .el-select__placeholder,
  .el-select__selected-item {
    height: 32px;
    color: #555d62;
    line-height: 32px;
  }

  .keyword-input {
    width: 260px;
    flex: 0 0 260px;
  }

  .month-range {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 8px;

    .el-date-editor {
      width: 136px;
      height: 34px;
      flex: 0 0 136px;
    }

    .el-date-editor .el-input__wrapper {
      display: flex;
      padding: 1px 10px !important;
    }

    .el-date-editor .el-input__prefix {
      position: static !important;
      width: 14px;
      flex: 0 0 14px;
      margin: 0 8px 0 0 !important;
      transform: none !important;
    }

    .el-input__inner {
      width: 0;
      min-width: 0;
      flex: 1;
      padding: 0 !important;
    }
  }

  > .el-select {
    width: 132px;
    min-width: 112px;
    flex: 1 1 132px;
  }

  .date-split {
    color: #9ba2a7;
    font-size: 12px;
    white-space: nowrap;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 4px;
    margin-left: 2px;
  }

  .query-btn,
  .reset-btn,
  .collapse-btn {
    height: 32px;
    padding: 0 12px;
    flex: 0 0 auto;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #00aaa4;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s;

    &:hover {
      background: #eaf8f7;
    }
  }

  .query-btn {
    padding: 0 18px;
    background: #00aaa4;
    color: #fff;

    &:hover {
      background: #079b96;
    }
  }

  .collapse-btn {
    color: #73777a;

    span {
      display: inline-block;
      margin-left: 3px;
      transition: transform 0.2s;
    }
  }
}

.rotated {
  transform: rotate(180deg);
}

.supplier-content {
  min-height: 120px;
  margin: 0 18px;
}

.supplier-load-trigger {
  padding: 16px 0 6px;
  color: #999da0;
  font-size: 13px;
  text-align: center;
}

.supplier-card {
  position: relative;
  margin-bottom: 10px;
  overflow: hidden;
  border-radius: 8px;
  background: #fff;

  &--recommended {
    margin-bottom: 8px;
    border: 2px solid #ffc2c2;
  }

  &.is-collapsed {
    min-height: 0;
  }

  &--recommended:not(.is-collapsed) {
    min-height: 500px;
  }

  &:not(.supplier-card--recommended):not(.is-collapsed) {
    min-height: 455px;
  }
}

.recommend-ribbon {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  width: 101px;
  height: 28px;
  padding-left: 12px;
  box-sizing: border-box;
  background: linear-gradient(135deg, #e42828 0 77%, transparent 78%);
  color: #fff;
  font-size: 11px;
  line-height: 28px;
}

.supplier-card__inner {
  position: relative;
  width: 100%;
  height: 100%;
}

.supplier-card__scroll {
  height: 100%;
  margin-right: 83px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-color: #aeb4b8 #edf0f2;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 5px;
    background: #edf0f2;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid #edf0f2;
    border-radius: 5px;
    background: #aeb4b8;

    &:hover {
      background: #858d92;
    }
  }
}

.supplier-card__scroll-content {
  width: 100%;
  min-width: 100%;
  min-height: 100%;
  box-sizing: border-box;
}

.supplier-card__header {
  display: flex;
  align-items: center;
  min-height: 48px;
  margin: 0 20px;
  box-sizing: border-box;
  border-bottom: 1px solid #dde4e8;

  .company-title {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    min-width: 460px;
    white-space: nowrap;

    strong {
      margin-right: 16px;
      color: #373b3e;
      font-size: 20px;
      font-weight: 500;
    }
  }
}

.supplier-card--recommended .supplier-card__header {
  min-height: 76px;
  padding-top: 28px;
}

.supplier-card.is-collapsed .supplier-card__header {
  height: 48px;
  min-height: 48px;
  padding-top: 0;
  border-bottom: 0;
}

.supplier-card--recommended.is-collapsed .supplier-card__header {
  height: 76px;
  min-height: 76px;
  padding-top: 28px;
}

.supplier-tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-right: 8px;
  padding: 0 8px;
  border-radius: 5px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 20px;

  &--green { color: #fff; background: #12bf69; }
  &--red { color: #ff4b59; background: #ffe9eb; }
  &--blue { color: #1886ff; border: 1px solid #1886ff; background: #fff; }
  &--yellow { color: #fff; background: #f2b500; }
  &--cyan { color: #2388c6; background: #dff4ff; }
  &--orange { color: #eaa000; border: 1px solid #f2b500; background: #fff; border-radius: 11px; }
  &--gray { color: #fff; background: #bfc2c4; }
  &--purple { color: #7848e8; background: #eee8ff; }
  &--outline-red { color: #ff3e4d; border: 1px solid #ff3e4d; background: #fff; border-radius: 11px; }
}

.supplier-stats {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: 12px;
  color: #95999c;
  white-space: nowrap;

  span {
    flex: 0 0 auto;
    margin-right: 22px;
    font-size: 14px;

    b {
      color: #555a5d;
      font-weight: 500;
    }
  }
}

.card-toggle {
  position: absolute;
  z-index: 4;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  width: 83px;
  padding-top: 14px;
  align-items: flex-start;
  justify-content: center;
  border: 0;
  border-radius: 0 6px 6px 0;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.12);
  background: #fff;
  color: #676c6f;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;

  span {
    display: inline-block;
    transition: transform 0.2s;
  }
}

.supplier-card--recommended:not(.is-collapsed) .card-toggle {
  padding-top: 42px;
}

.supplier-card.is-collapsed .card-toggle {
  bottom: auto;
  height: 48px;
  padding-top: 0;
  align-items: center;
}

.supplier-card--recommended.is-collapsed .card-toggle {
  top: 28px;
}

.detail-tabs {
  display: flex;
  height: 48px;
  margin: 0 20px;
  align-items: flex-end;
  border-bottom: 1px solid #d9dddf;

  button {
    height: 34px;
    width: 90px;
    min-width: 90px;
    margin-right: 4px;
    padding: 0;
    border: 0;
    border-radius: 5px 5px 0 0;
    background: #fff;
    color: #6e7376;
    cursor: pointer;

    &.active {
      border: 1px solid #d9dddf;
      border-bottom-color: #fff;
      color: #00aaa4;
      background: #fff;
      font-weight: 600;
    }
  }
}

.supplier-detail {
  min-height: 359px;
  padding: 24px 32px 18px;
  box-sizing: border-box;
  color: #51565a;
  font-size: 13px;

  .info-row {
    margin-bottom: 10px;
    line-height: 18px;

    > span {
      display: inline-block;
      width: 78px;
      color: #aaaeb1;
    }
  }

}

.supplier-card--recommended .supplier-detail {
  min-height: 376px;
}

.contact-list {
  display: grid;
  max-height: 330px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  padding-right: 8px;
  overflow-y: auto;
  scrollbar-color: #aeb4b8 #edf0f2;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 4px;
    background: #edf0f2;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: #aeb4b8;
  }
}

.contact-item {
  padding: 12px 14px;
  border: 1px solid #e1e5e8;
  border-radius: 4px;
  background: #fafcfc;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    strong {
      color: #373b3e;
      font-size: 15px;
      font-weight: 600;
    }

    span {
      color: #00aaa4;
      font-size: 13px;
      font-weight: 600;
    }
  }

  .info-row {
    margin-bottom: 4px;
  }
}

.license-row {
  display: flex;
  margin-bottom: 15px;

  > span {
    flex: 0 0 78px;
    color: #aaaeb1;
  }
}

.attachment-empty {
  color: #51565a;
  line-height: 20px;
}

.attachment-image-list,
.qualification-gallery {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-image {
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid #e1e5e8;
  border-radius: 4px;
  background: #f5f7f8;
  cursor: pointer;

  &--license {
    width: 160px;
    height: 100px;
  }

  &--qualification {
    width: 180px;
    height: 120px;
  }
}

.qualification-gallery {
  align-content: flex-start;
}

/*
 * 代理品牌以网格卡片展示；数据较多时仅在该区域滚动，避免单张供应商卡片持续拉长。
 * 每个品牌的证书列表保持横向滚动，避免多张证书再次把品牌卡片撑高。
 */
.agent-brand-list {
  display: grid;
  max-height: 310px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  padding-right: 8px;
  overflow-y: auto;
  scrollbar-color: #aeb4b8 #edf0f2;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 4px;
    background: #edf0f2;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: #aeb4b8;
  }
}

.agent-brand-item {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e1e5e8;
  border-radius: 4px;
  background: #fafcfc;

  .brand-row {
    margin-top: 0;
  }

  &__images {
    margin: 8px 0 0 78px;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-color: #aeb4b8 #edf0f2;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 3px;
      background: #aeb4b8;
    }

    .attachment-image--qualification {
      width: 140px;
      height: 94px;
    }
  }

  .attachment-empty {
    margin: 8px 0 0 78px;
  }
}

.business-brand-list {
  display: grid;
  max-height: 290px;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  column-gap: 28px;
  row-gap: 8px;
  padding-right: 8px;
  overflow-y: auto;
  scrollbar-color: #aeb4b8 #edf0f2;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 5px;
    background: #edf0f2;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid #edf0f2;
    border-radius: 5px;
    background: #aeb4b8;

    &:hover {
      background: #858d92;
    }
  }
}

.business-brand-item {
  position: relative;
  min-width: 0;
  padding-left: 12px;
  overflow: hidden;
  color: #51565a;
  font-size: 13px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    position: absolute;
    top: 9px;
    left: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #00aaa4;
    content: "";
  }
}

.panlei-statistics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 40px;
  row-gap: 14px;

  &__item {
    display: flex;
    min-width: 0;
    padding: 0 0 9px;
    align-items: baseline;
    justify-content: space-between;
    box-sizing: border-box;
    border-bottom: 1px dashed #e5e9ec;

    span {
      color: #95999c;
      font-size: 13px;
    }

    b {
      color: #00aaa4;
      font-size: 18px;
      font-weight: 600;
    }
  }
}

.license-mock {
  position: relative;
  display: flex;
  width: 149px;
  height: 100px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #aca795;
  background:
    linear-gradient(rgba(232, 228, 209, 0.84), rgba(232, 228, 209, 0.84)),
    repeating-linear-gradient(0deg, #8f8976 0 1px, transparent 1px 5px);
  color: #695f4c;
  transform: rotate(-0.5deg);

  .license-emblem {
    color: #b43b32;
    font-size: 15px;
  }

  b {
    font-family: SimSun, serif;
    font-size: 13px;
    letter-spacing: 4px;
  }

  small {
    margin-top: 5px;
    font-size: 6px;
  }

  i {
    margin-top: 4px;
    font-size: 6px;
    font-style: normal;
  }

  em {
    position: absolute;
    right: 8px;
    bottom: 8px;
    width: 35px;
    height: 35px;
    border: 2px solid rgba(183, 48, 40, 0.72);
    border-radius: 50%;
    color: rgba(183, 48, 40, 0.72);
    font-size: 5px;
    font-style: normal;
    line-height: 33px;
    text-align: center;
    transform: rotate(-15deg);
  }
}

.brand-row {
  display: flex;
  margin-top: 8px;
  line-height: 27px;

  > span {
    flex: 0 0 78px;
    color: #aaaeb1;
  }

  &__content {
    min-width: 0;
    flex: 1 1 auto;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}

.tab-placeholder {
  display: flex;
  height: 260px;
  align-items: center;
  justify-content: center;
  color: #a8acae;
}

.list-title {
  margin: 24px 0 16px;
  color: #777b7e;
  font-size: 18px;
  font-weight: 500;
}

.region-supplier-count {
  margin-left: 8px;
  color: #909399;
  font-size: 14px;
  font-weight: 400;
}

.empty-result {
  padding: 48px 0;
  border-radius: 8px;
  background: #fff;
  color: #a9adaf;
  text-align: center;
}

@media (max-width: 1500px) {
  .brand-filter__links {
    gap: 10px;
  }

  .filter-field--brand {
    width: 340px;

    .brand-input-shell {
      width: 280px;
    }
  }

  .filter-field--series {
    width: 230px;

    .vue-treeselect {
      width: 185px;
    }
  }

  .supplier-card__header .company-title {
    min-width: 410px;
  }

  .supplier-stats span {
    margin-right: 15px;
  }

  .search-toolbar {
    flex-wrap: wrap;

    > .el-select {
      flex: 1 1 128px;
    }

    .toolbar-actions {
      margin-left: auto;
    }
  }
}
</style>
